import { useMemo } from 'react';
import { Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { activeMapCountryConfig } from '@/data/active-country';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/lib/auth-client';
import { pins as pinsApi, type Pin } from '@/lib/api';
import { DATA_BASE_URL, queries } from '@/data';
import { useDetailsStore } from '@/features/details';
import { useUnitTerms } from '@/features/map-core/useUnitTerms';
import { usePinsStore, type PinsMode } from './store';

/**
 * F5.3 — Pins layer.
 *
 * Two sources, blended based on `mode`:
 *   - personal:  /api/pins   (requires auth; user's own pins)
 *   - demo:      /data/example_pins.json  (anonymous-friendly sample listings)
 *
 * Default mode `auto` shows personal when signed in, demo when anonymous —
 * so a first-time visitor lands on a populated map without surfacing real
 * user data, and a returning signed-in user sees their own list.
 *
 * Demo markers are drawn with a softer (translucent) purple + dashed
 * outline so they read as "examples", not as your real shortlist.
 */

interface DemoPin {
  id: string;
  title: string;
  url?: string;
  lat: number;
  lon: number;
  type?: 'idealista' | 'custom';
  notes?: string;
}

interface DemoFile {
  version?: number;
  items: DemoPin[];
}

function makePinIcon(opts: { color: string; dashed: boolean; emoji: string }): L.DivIcon {
  const border = opts.dashed ? '2px dashed rgba(255,255,255,0.8)' : 'none';
  return L.divIcon({
    html: `<div style="
      display: flex;
      align-items: center;
      justify-content: center;
      width: 28px;
      height: 28px;
      border-radius: 14px 14px 14px 2px;
      background: ${opts.color};
      color: white;
      font-size: 14px;
      transform: rotate(-45deg);
      box-shadow: 0 2px 6px rgba(0,0,0,0.35);
      box-sizing: border-box;
      border: ${border};
    "><span style="transform: rotate(45deg)">${opts.emoji}</span></div>`,
    className: '',
    iconSize: [28, 28],
    iconAnchor: [4, 28],
  });
}

const PERSONAL_ICON = makePinIcon({ color: '#6b21a8', dashed: false, emoji: '📌' });
const DEMO_ICON = makePinIcon({ color: 'rgba(107, 33, 168, 0.55)', dashed: true, emoji: '🏠' });

function resolveSources(mode: PinsMode, signedIn: boolean): {
  fetchMine: boolean;
  fetchDemo: boolean;
} {
  switch (mode) {
    case 'mine-only':
      return { fetchMine: signedIn, fetchDemo: false };
    case 'demo-only':
      return { fetchMine: false, fetchDemo: true };
    case 'both':
      return { fetchMine: signedIn, fetchDemo: true };
    case 'hidden':
      return { fetchMine: false, fetchDemo: false };
    case 'auto':
    default:
      return { fetchMine: signedIn, fetchDemo: !signedIn };
  }
}

export function PinsLayer() {
  const { t } = useTranslation();
  const { data: session } = useSession();
  const mode = usePinsStore((s) => s.mode);
  const qc = useQueryClient();
  const openDetails = useDetailsStore((s) => s.open);

  const { fetchMine, fetchDemo } = resolveSources(mode, !!session);

  const { data: meta } = useQuery(queries.meta());
  const { data: fullIndex } = useQuery({
    ...queries.fullIndex(meta?.version ?? ''),
    enabled: !!meta?.version && fetchMine,
  });

  const mineQuery = useQuery({
    queryKey: ['pins'],
    queryFn: () => pinsApi.list(),
    enabled: fetchMine,
    staleTime: 30 * 1000,
  });

  const demoQuery = useQuery<DemoFile>({
    queryKey: ['pins-demo'],
    queryFn: async () => {
      const res = await fetch(`${DATA_BASE_URL}/example_pins.json`);
      if (!res.ok) throw new Error(`example_pins.json: ${res.status}`);
      return res.json();
    },
    enabled: fetchDemo,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => pinsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pins'] }),
  });

  const personal = useMemo<Pin[]>(() => (mineQuery.data ?? []).filter(validCoords), [mineQuery.data]);
  const demo = useMemo<DemoPin[]>(
    () => (demoQuery.data?.items ?? []).filter(validCoords),
    [demoQuery.data],
  );

  if (mode === 'hidden') return null;
  if (personal.length === 0 && demo.length === 0) return null;

  return (
    <>
      {personal.map((p) => {
        const linkedName = p.comuneIstat
          ? fullIndex?.byIstat.get(p.comuneIstat)?.name ?? null
          : null;
        return (
          <Marker key={`mine-${p.id}`} position={[p.lat, p.lon]} icon={PERSONAL_ICON}>
            <Popup>
              <PinPopupBody
                pin={p}
                linkedName={linkedName}
                onOpenComune={
                  p.comuneIstat ? () => openDetails(p.comuneIstat!) : undefined
                }
                onDelete={() => deleteMutation.mutate(p.id)}
                t={t}
              />
            </Popup>
          </Marker>
        );
      })}
      {demo.map((p) => (
        <Marker key={`demo-${p.id}`} position={[p.lat, p.lon]} icon={DEMO_ICON}>
          <Popup>
            <PinPopupBody pin={p} isDemo t={t} />
          </Popup>
        </Marker>
      ))}
    </>
  );
}

function PinPopupBody({
  pin,
  isDemo,
  linkedName,
  onOpenComune,
  onDelete,
  t,
}: {
  pin: Pin | DemoPin;
  isDemo?: boolean;
  linkedName?: string | null;
  onOpenComune?: () => void;
  onDelete?: () => void;
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const setMode = usePinsStore((s) => s.setMode);
  const terms = useUnitTerms();

  return (
    <div className="min-w-[210px] space-y-1 text-xs">
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-slate-900">{pin.title}</div>
        {isDemo && (
          <span
            className="shrink-0 rounded bg-purple-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-purple-800"
          >
            {t('pins.demoBadge', { defaultValue: 'demo' })}
          </span>
        )}
      </div>
      {pin.notes && <div className="text-slate-600">{pin.notes}</div>}
      {pin.url && (
        <a
          href={pin.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block truncate text-[color:var(--color-terra-600)] underline"
        >
          {pin.url}
        </a>
      )}
      {onOpenComune && (
        <button
          type="button"
          onClick={onOpenComune}
          className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-[color:var(--color-terra-600)] hover:text-[color:var(--color-terra-700)] hover:underline"
          title={t('pins.openComune', { ...terms, defaultValue: 'Open {{unit}} metrics' })}
        >
          📊 {linkedName
            ? t('pins.openComuneNamed', {
                name: linkedName,
                defaultValue: 'Metrics for {{name}}',
              })
            : t('pins.openComune', { ...terms, defaultValue: 'Open {{unit}} metrics' })}
        </button>
      )}
      {isDemo && (
        <div className="mt-3 rounded-md border border-purple-200 bg-purple-50 p-2.5 space-y-1.5 text-[11px] leading-relaxed text-slate-600">
          <p>
            <strong className="text-slate-800">
              {t('pins.demoWhat', { defaultValue: 'Что это?' })}
            </strong>{' '}
            {t('pins.demoWhatBody', {
              defaultValue:
                'Демо-пин — пример объявления. Так будут выглядеть ваши сохранённые места.',
            })}
          </p>
          <p>
            <strong className="text-slate-800">
              {t('pins.demoWhy', { defaultValue: 'Почему здесь?' })}
            </strong>{' '}
            {t('pins.demoWhyBody', {
              defaultValue:
                'Карта показывает демо-данные анонимным пользователям, чтобы она не была пустой.',
            })}
          </p>
          <p>
            <strong className="text-slate-800">
              {t('pins.demoHow', { defaultValue: 'Как пользоваться?' })}
            </strong>{' '}
            {t('pins.demoHowBody', {
              defaultValue:
                'Войдите в аккаунт и сохраняйте понравившиеся места — они появятся здесь с оценками по климату и инфраструктуре.',
            })}
          </p>
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button
              type="button"
              onClick={() => setMode('hidden')}
              className="rounded border border-slate-300 bg-white px-2.5 py-1 text-[10px] font-semibold text-slate-600 transition-colors hover:border-red-400 hover:text-red-600"
            >
              {t('pins.demoHide', { defaultValue: '✕ Скрыть демо-пины' })}
            </button>
            <a
              href="/login"
              className="text-[10px] font-semibold text-purple-700 underline hover:text-purple-900"
            >
              {t('pins.demoSignIn', { defaultValue: 'Войти в аккаунт →' })}
            </a>
          </div>
        </div>
      )}
      {onDelete && !isDemo && (
        <button
          type="button"
          onClick={() => {
            if (window.confirm(t('pins.deleteConfirm', { defaultValue: 'Delete this pin?' }))) {
              onDelete();
            }
          }}
          className="mt-1 text-[10px] text-red-600 underline hover:text-red-700"
        >
          {t('pins.delete', { defaultValue: 'Delete' })}
        </button>
      )}
    </div>
  );
}

// Sanity-check по bbox активной страны: отсекает мусорные координаты из
// импорта и геокодинга. Раньше bbox Италии был зашит числами.
function validCoords(p: { lat: number; lon: number }): boolean {
  const [west, south, east, north] = activeMapCountryConfig().bbox;
  return (
    typeof p.lat === 'number' &&
    typeof p.lon === 'number' &&
    p.lat >= south &&
    p.lat <= north &&
    p.lon >= west &&
    p.lon <= east
  );
}
